import React, { useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  Hidden,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core'
import {
  HelpCircle as HelpCircleIcon,
  Inbox,
  PlusCircle,
  Search,
} from 'react-feather'
import { generatePath } from 'react-router-dom'
import { isEmpty } from 'lodash'

import {
  GuestDriveTour,
  LoadingFeedback,
  MenuButton,
  NewAiInventoryDialog,
} from 'components'

import useDaiPermissions from 'hooks/useDaiPermissions'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import { routes } from 'GuestRoutes'
import useStyles from './styles'
import {
  AI_INVENTORY_DRIVER_STEP_HEADER,
  AI_INVENTORY_DRIVER_STEP_NEW,
  AI_INVENTORY_DRIVER_STEP_SEARCH,
  AI_INVENTORY_DRIVER_STEP_TABLE,
  AI_INVENTORY_GUEST_DRIVER_STEPS,
} from './driverSteps'

const AiInventoryList = ({ history, match }) => {
  const [allItems, setAllItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [openTour, setOpenTour] = useState(false)
  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { organizationSlug } = match.params
  const { permitted } = useDaiPermissions()
  const daiAllowed = permitted(constants.dai.solutions.TAGS.AI_INVENTORY)

  const goToManualRegistration = () =>
    history.push(generatePath(routes.aiInventory.new, { organizationSlug }))

  const goToDaiRegistration = () =>
    history.push(generatePath(routes.aiInventory.daiNew, { organizationSlug }))

  // Com a DAI bloqueada por opt-out, só resta o cadastro manual — vai direto,
  // sem o diálogo de escolha.
  const handleNewAiInventory = () =>
    daiAllowed ? setNewDialogOpen(true) : goToManualRegistration()

  const loadItems = async () => {
    setIsLoading(true)
    try {
      const response = await service.guestPortal.aiInventory.list({
        perPage: 1000,
      })
      setAllItems(response?.data?.aiInventoryItems || [])
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao carregar inventários.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadItems()
    // eslint-disable-next-line
  }, [])

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems
    const query = searchQuery.trim().toLowerCase()
    return allItems.filter((item) => item.name?.toLowerCase().includes(query))
  }, [allItems, searchQuery])

  const paginatedItems = useMemo(() => {
    const start = page * perPage
    return filteredItems.slice(start, start + perPage)
  }, [filteredItems, page, perPage])

  return (
    <Box>
      <LoadingFeedback open={isLoading} />
      <Box id={AI_INVENTORY_DRIVER_STEP_HEADER} className={classes.header}>
        <Typography variant="h3">Inventário de IA</Typography>
        <Box className={classes.headerActions}>
          <Button
            id={AI_INVENTORY_DRIVER_STEP_NEW}
            variant="contained"
            color="secondary"
            startIcon={<PlusCircle size={18} />}
            onClick={handleNewAiInventory}
          >
            Nova IA
          </Button>
          <Button
            variant="outlined"
            startIcon={<HelpCircleIcon size={18} />}
            onClick={() => setOpenTour(true)}
          >
            Tutorial
          </Button>
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.filtersRow}>
          <TextField
            id={AI_INVENTORY_DRIVER_STEP_SEARCH}
            placeholder="Pesquisar..."
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value)
              setPage(0)
            }}
            variant="outlined"
            size="small"
            className={classes.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          id={AI_INVENTORY_DRIVER_STEP_TABLE}
          className={classes.tableWrapper}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Provedor</TableCell>
                <TableCell>Última Alteração</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isEmpty(paginatedItems) ? (
                <TableRow>
                  <TableCell colSpan={4} padding="none">
                    <Box className={classes.emptyState}>
                      <Inbox size={36} color="#AAB8C2" />
                      <Typography variant="body2" color="textSecondary">
                        Nenhum inventário encontrado.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box className={classes.iconCell}>
                        {item.aiInventoryTool?.iconUrl && (
                          <img
                            src={item.aiInventoryTool.iconUrl}
                            alt=""
                            className={classes.toolIcon}
                          />
                        )}
                        {item.name || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className={classes.iconCell}>
                        {item.aiInventoryTool?.aiInventoryProvider?.iconUrl && (
                          <img
                            src={
                              item.aiInventoryTool.aiInventoryProvider.iconUrl
                            }
                            alt=""
                            className={classes.toolIcon}
                          />
                        )}
                        {item.aiInventoryTool?.aiInventoryProvider?.name || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {helpers.formatters.ptBrFormatDateTime(item.updatedAt)}
                    </TableCell>
                    <TableCell align="right">
                      <MenuButton>
                        <Button
                          color="secondary"
                          fullWidth
                          size="small"
                          onClick={() =>
                            history.push(
                              generatePath(routes.aiInventory.show, {
                                organizationSlug,
                                aiInventoryId: item.id,
                              }),
                            )
                          }
                        >
                          Visualizar
                        </Button>
                        <Button
                          color="secondary"
                          fullWidth
                          size="small"
                          onClick={() =>
                            history.push(
                              generatePath(routes.aiInventory.edit, {
                                organizationSlug,
                                aiInventoryId: item.id,
                              }),
                            )
                          }
                        >
                          Editar
                        </Button>
                      </MenuButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={filteredItems.length}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          page={page}
          rowsPerPage={perPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={<Hidden smDown>Por página</Hidden>}
          nextIconButtonProps={{ size: 'small' }}
          backIconButtonProps={{ size: 'small' }}
          className={classes.pagination}
        />
      </Box>

      <GuestDriveTour
        stepsMatrix={AI_INVENTORY_GUEST_DRIVER_STEPS}
        open={openTour}
        setOpen={setOpenTour}
      />

      <NewAiInventoryDialog
        open={newDialogOpen}
        setOpen={setNewDialogOpen}
        onSelectDai={goToDaiRegistration}
        onSelectManual={goToManualRegistration}
      />
    </Box>
  )
}

export default AiInventoryList
